import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type AuthCtx = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
};

const Ctx = createContext<AuthCtx>({ user: null, session: null, loading: true, signOut: async () => {} });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    // 1) Hydrate from storage FIRST so the UI doesn't flash logged-out.
    supabase.auth.getSession()
      .then(({ data }) => {
        if (!mounted) return;
        setSession(data.session);
        setLoading(false);
      })
      .catch((err) => {
        if (!mounted) return;
        // Don't wipe session on cosmetic refresh errors — let the listener decide.
        if (err?.code !== "refresh_token_not_found") {
          console.warn("[auth] getSession error:", err?.message);
        }
        setLoading(false);
      });

    // 2) Then subscribe to future changes.
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      // Ignore transient INITIAL_SESSION null when we already hydrated.
      if (event === "INITIAL_SESSION" && s === null) return;
      setSession(s);
      setLoading(false);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <Ctx.Provider value={{ user: session?.user ?? null, session, loading, signOut }}>
      {children}
    </Ctx.Provider>
  );
}

export const useAuth = () => useContext(Ctx);
